import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { supabase } from '../lib/supabase'
import PrivateImage from '../components/PrivateImage'

const MEASURE_LABELS = {
  chest:'Chest', waist:'Waist', hips:'Hips', shoulders:'Shoulders', back_length:'Back Length',
  front_length:'Front Length', sleeve_length:'Sleeve', sleeve_right:'Sleeve (R)', sleeve_left:'Sleeve (L)',
  bicep:'Bicep', wrist:'Wrist', neck:'Neck', trouser_waist:'Trouser Waist', seat:'Seat', thigh:'Thigh',
  knee:'Knee', calf:'Calf', inseam:'Inseam', outseam:'Outseam', bottom_width:'Bottom', rise_front:'Rise (F)',
  rise_back:'Rise (B)', collar:'Collar', shirt_chest:'Shirt Chest', shirt_waist:'Shirt Waist',
  shirt_length:'Shirt Length', shirt_sleeve:'Shirt Sleeve', height:'Height',
}
const cleanSearch = v => v.replace(/[,()%*]/g, ' ').trim()

const STATUS_LABELS = {
  new_lead:'New Lead', contacted:'Contacted', meeting_confirmed:'Meeting Set',
  measurements_taken:'Measured', order_confirmed:'Confirmed',
  in_production:'In Production', quality_check:'Quality Check',
  shipped:'Shipped', delivered:'Delivered', cancelled:'Cancelled'
}
const STATUS_COLORS = {
  new_lead:'gray', contacted:'blue', meeting_confirmed:'blue',
  measurements_taken:'amber', order_confirmed:'amber',
  in_production:'gold', quality_check:'gold',
  shipped:'green', delivered:'green', cancelled:'red'
}
const PIPELINE = ['new_lead','measurements_taken','order_confirmed','in_production','quality_check','shipped','delivered']

export function Orders() {
  const [orders, setOrders] = useState([])
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('active')
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const t = setTimeout(fetchOrders, 300)
    return () => clearTimeout(t)
  }, [search, filter])

  async function fetchOrders() {
    setLoading(true)
    let q = supabase.from('orders').select('*, clients(first_name, last_name)').order('created_at', { ascending: false })
    if (filter === 'active') q = q.not('status', 'in', '("delivered","cancelled")').eq('is_historical', false)
    else if (filter === 'delivered') q = q.eq('status', 'delivered')
    else if (filter === 'old books') q = q.eq('is_historical', true)
    const term = cleanSearch(search)
    if (term) q = q.or(`order_number.ilike.%${term}%,legacy_order_number.ilike.%${term}%,description.ilike.%${term}%`)
    const { data } = await q.limit(50)
    setOrders(data || [])
    setLoading(false)
  }

  return (
    <div>
      <div className="page-header" style={{ justifyContent: 'space-between' }}>
        <h1 className="page-title">Orders</h1>
        <Link to="/orders/new" className="btn btn-primary btn-sm"><Plus size={16} /> New</Link>
      </div>
      <div className="search-bar">
        <Search size={16} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search order no., dad's order no. or description…" />
      </div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
        {['active','all','delivered','old books'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '5px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer',
            fontSize: '12px', fontWeight: '500', textTransform: 'capitalize',
            background: filter === f ? 'var(--brown-dark)' : 'var(--white)',
            color: filter === f ? 'var(--gold)' : 'var(--gray-600)',
            boxShadow: 'var(--shadow)'
          }}>{f}</button>
        ))}
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? <div className="empty-state"><p style={{ color: 'var(--gray-400)' }}>Loading…</p></div>
        : orders.length === 0 ? (
          <div className="empty-state" style={{ padding: '32px' }}>
            <p className="empty-state-title">No orders found</p>
          </div>
        ) : orders.map(order => (
          <Link key={order.id} to={`/orders/${order.id}`} className="list-item">
            <div className="list-item-avatar" style={{ borderRadius: '8px', fontSize: '11px', fontWeight: '700', width: '38px', height: '38px' }}>
              {order.garment_type?.substring(0,3).toUpperCase()}
            </div>
            <div className="list-item-content">
              <div className="list-item-title">
                {order.order_number}
                {order.legacy_order_number && <span style={{ fontSize: '11px', color: 'var(--gold)', fontWeight: '600', marginLeft: '6px' }}>#{order.legacy_order_number}</span>}
              </div>
              <div className="list-item-sub">
                {order.clients ? `${order.clients.first_name||''} ${order.clients.last_name||''}`.trim() : 'No client'}
                {order.is_historical && order.order_date ? ` · ${new Date(order.order_date).getFullYear()}` : ''}
                {order.trunk_show_city ? ` · ${order.trunk_show_city}` : ''}
              </div>
            </div>
            <div className="list-item-right">
              <span className={`badge badge-${STATUS_COLORS[order.status]||'gray'}`} style={{ fontSize: '9px' }}>
                {STATUS_LABELS[order.status]||order.status}
              </span>
              {order.expected_delivery && (
                <span style={{ fontSize: '10px', color: 'var(--gray-400)' }}>
                  {new Date(order.expected_delivery).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}

export function OrderDetail() {
  const { id } = useParams()
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [measurement, setMeasurement] = useState(null)
  const [linked, setLinked] = useState([])       // orders this page refers to
  const [referencedBy, setReferencedBy] = useState([]) // orders that refer to this one
  const navigate = useNavigate()

  useEffect(() => {
    supabase.from('orders').select('*, clients(first_name, last_name, email, phone), fabrics(fabric_code, name, color, image_url)').eq('id', id).single()
      .then(({ data }) => { setOrder(data); setLoading(false) })
  }, [id])

  useEffect(() => {
    if (!order) return
    const mq = order.measurement_id
      ? supabase.from('measurements').select('*').eq('id', order.measurement_id).maybeSingle()
      : supabase.from('measurements').select('*').eq('order_id', order.id).order('measured_at', { ascending: false }).limit(1).maybeSingle()
    mq.then(({ data }) => setMeasurement(data || null))

    const nums = order.linked_order_numbers || []
    if (nums.length) {
      supabase.from('orders').select('id, order_number, legacy_order_number, order_date').in('legacy_order_number', nums)
        .then(({ data }) => setLinked(nums.map(n => ({ number: n, order: (data || []).find(o => o.legacy_order_number === n) || null }))))
    } else setLinked([])

    if (order.legacy_order_number) {
      supabase.from('orders').select('id, order_number, legacy_order_number, order_date')
        .contains('linked_order_numbers', [order.legacy_order_number]).neq('id', order.id)
        .then(({ data }) => setReferencedBy(data || []))
    } else setReferencedBy([])
  }, [order])

  async function updateStatus(newStatus) {
    setUpdatingStatus(true)
    await supabase.from('orders').update({ status: newStatus }).eq('id', id)
    setOrder(p => ({ ...p, status: newStatus }))
    setUpdatingStatus(false)
  }

  if (loading) return <div className="empty-state"><p style={{ color: 'var(--gray-400)' }}>Loading…</p></div>
  if (!order) return <div className="empty-state"><p>Order not found</p></div>

  const currentIdx = PIPELINE.indexOf(order.status)

  return (
    <div>
      <div className="page-header">
        <button className="page-header-back" onClick={() => navigate(-1)}>←</button>
        <div style={{ flex: 1 }}>
          <h1 className="page-title" style={{ fontSize: '18px' }}>
            {order.order_number}
            {order.legacy_order_number && <span style={{ fontSize: '13px', color: 'var(--gold)', marginLeft: '8px' }}>Book #{order.legacy_order_number}</span>}
          </h1>
          <span className={`badge badge-${STATUS_COLORS[order.status]||'gray'}`} style={{ fontSize: '10px', marginTop: '2px' }}>
            {STATUS_LABELS[order.status]}
          </span>
        </div>
        <Link to="/invoices/new" className="btn btn-outline btn-sm">Invoice</Link>
      </div>

      {/* Pipeline */}
      <div className="card" style={{ marginBottom: '12px', overflow: 'hidden' }}>
        <div className="form-section-title" style={{ marginBottom: '12px' }}>Progress</div>
        <div className="pipeline">
          {PIPELINE.map((step, idx) => (
            <div key={step} className={`pipeline-step ${idx < currentIdx ? 'done' : idx === currentIdx ? 'current' : ''}`}>
              <div className="pipeline-dot">{idx < currentIdx ? '✓' : idx + 1}</div>
              <div className="pipeline-step-label">{STATUS_LABELS[step]?.split(' ')[0]}</div>
            </div>
          ))}
        </div>
        {/* Status update buttons */}
        {order.status !== 'delivered' && order.status !== 'cancelled' && (
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {currentIdx < PIPELINE.length - 1 && (
              <button className="btn btn-primary btn-sm" onClick={() => updateStatus(PIPELINE[currentIdx + 1])} disabled={updatingStatus}>
                → {STATUS_LABELS[PIPELINE[currentIdx + 1]]}
              </button>
            )}
            <button className="btn btn-danger btn-sm" onClick={() => updateStatus('cancelled')} disabled={updatingStatus}>Cancel Order</button>
          </div>
        )}
      </div>

      {/* Client */}
      {order.clients && (
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Client</div>
          <Link to={`/clients/${order.client_id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="list-item-avatar" style={{ width: '36px', height: '36px', fontSize: '14px' }}>
              {(order.clients.first_name||'').charAt(0)}{(order.clients.last_name||'').charAt(0)}
            </div>
            <div>
              <div style={{ fontWeight: '600' }}>{order.clients.first_name} {order.clients.last_name}</div>
              <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>{order.clients.email}</div>
            </div>
            <span style={{ marginLeft: 'auto', color: 'var(--gray-300)', fontSize: '18px' }}>›</span>
          </Link>
        </div>
      )}

      {/* Garment details */}
      <div className="card" style={{ marginBottom: '12px' }}>
        <div className="form-section-title">Order Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div><div style={{ fontSize: '11px', color: 'var(--gray-400)', fontWeight: '600', textTransform: 'uppercase' }}>Garment</div><div style={{ fontWeight: '500', textTransform: 'capitalize' }}>{order.garment_type}</div></div>
          <div><div style={{ fontSize: '11px', color: 'var(--gray-400)', fontWeight: '600', textTransform: 'uppercase' }}>Qty</div><div style={{ fontWeight: '500' }}>{order.quantity}</div></div>
          {order.order_date && <div><div style={{ fontSize: '11px', color: 'var(--gray-400)', fontWeight: '600', textTransform: 'uppercase' }}>Order Date</div><div style={{ fontWeight: '500' }}>{new Date(order.order_date).toLocaleDateString()}</div></div>}
          {order.needed_by && <div><div style={{ fontSize: '11px', color: 'var(--gray-400)', fontWeight: '600', textTransform: 'uppercase' }}>Needed By</div><div style={{ fontWeight: '500', color: 'var(--red)' }}>{new Date(order.needed_by).toLocaleDateString()}</div></div>}
          {order.expected_delivery && <div><div style={{ fontSize: '11px', color: 'var(--gray-400)', fontWeight: '600', textTransform: 'uppercase' }}>Expected Delivery</div><div style={{ fontWeight: '500' }}>{new Date(order.expected_delivery).toLocaleDateString()}</div></div>}
          {order.trunk_show_city && <div><div style={{ fontSize: '11px', color: 'var(--gray-400)', fontWeight: '600', textTransform: 'uppercase' }}>Trunk Show</div><div style={{ fontWeight: '500' }}>{order.trunk_show_city}</div></div>}
          {order.price && <div><div style={{ fontSize: '11px', color: 'var(--gray-400)', fontWeight: '600', textTransform: 'uppercase' }}>Price</div><div style={{ fontWeight: '600', color: 'var(--brown-dark)' }}>{order.currency} {Number(order.price).toLocaleString()}</div></div>}
          {order.deposit_paid && <div><div style={{ fontSize: '11px', color: 'var(--gray-400)', fontWeight: '600', textTransform: 'uppercase' }}>Deposit</div><div style={{ fontWeight: '500' }}>{order.currency} {Number(order.deposit_paid).toLocaleString()}</div></div>}
        </div>
        {order.description && <div style={{ marginTop: '12px', padding: '10px', background: 'var(--gray-50)', borderRadius: '8px', fontSize: '14px' }}>{order.description}</div>}
        {order.style_notes && (
          <div style={{ marginTop: '10px' }}>
            <div style={{ fontSize: '11px', color: 'var(--gray-400)', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>Style Notes</div>
            <div style={{ fontSize: '13px', color: 'var(--gray-700)', lineHeight: '1.5' }}>{order.style_notes}</div>
          </div>
        )}
      </div>

      {/* Book reference + linked orders */}
      {(order.is_historical || order.legacy_order_number || linked.length > 0 || referencedBy.length > 0) && (
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">From the Books</div>
          <div style={{ fontSize: '13px', color: 'var(--gray-700)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {order.legacy_order_number && <div><span style={{ color: 'var(--gray-400)' }}>Order no. on sheet: </span>#{order.legacy_order_number}</div>}
            {(order.source_book || order.source_page) && <div><span style={{ color: 'var(--gray-400)' }}>Source: </span>{[order.source_book, order.source_page && `page ${order.source_page}`].filter(Boolean).join(', ')}</div>}
          </div>
          {linked.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '11px', color: 'var(--gray-400)', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>Refers to</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {linked.map(l => l.order
                  ? <Link key={l.number} to={`/orders/${l.order.id}`} className="badge badge-gold" style={{ textDecoration: 'none' }}>#{l.number} · {l.order.order_number}</Link>
                  : <span key={l.number} className="badge badge-gray" title="Not imported yet">#{l.number} (not imported yet)</span>)}
              </div>
            </div>
          )}
          {referencedBy.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '11px', color: 'var(--gray-400)', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>Referred to by</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {referencedBy.map(o => <Link key={o.id} to={`/orders/${o.id}`} className="badge badge-blue" style={{ textDecoration: 'none' }}>#{o.legacy_order_number || o.order_number}</Link>)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Line items */}
      {Array.isArray(order.line_items) && order.line_items.length > 0 && (
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Items</div>
          {order.line_items.map((it, i) => (
            <div key={i} style={{ padding: '8px 0', borderTop: i ? '1px solid var(--gray-100, #f3f4f6)' : 'none', fontSize: '13px' }}>
              <div style={{ fontWeight: '600', textTransform: 'capitalize' }}>
                {it.qty || 1} × {it.garment_type}{it.description ? ` · ${it.description}` : ''}
                {it.price ? <span style={{ float: 'right' }}>{it.currency} {Number(it.price).toLocaleString()}</span> : null}
              </div>
              {it.style_notes && <div style={{ color: 'var(--gray-600)', marginTop: '2px' }}>{it.style_notes}</div>}
              {it.fabric_notes && <div style={{ color: 'var(--gray-500)', marginTop: '2px' }}>Fabric: {it.fabric_notes}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Measurements taken for this order */}
      {measurement && (
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">
            Measurements for this Order
            <span style={{ fontWeight: '400', color: 'var(--gray-400)', marginLeft: '6px' }}>{new Date(measurement.measured_at).toLocaleDateString()} · in</span>
          </div>
          <div className="measure-grid">
            {Object.entries(MEASURE_LABELS).filter(([k]) => measurement[k] != null).map(([k, label]) => (
              <div key={k} className="measure-field">
                <span className="measure-label">{label}</span>
                <span className="measure-value">{measurement[k]}<span className="measure-unit"> in</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fabric */}
      {(order.fabrics || order.fabric_notes || order.fabric_image_url) && (
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Fabric</div>
          {order.fabrics && (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
              {order.fabrics.image_url && <PrivateImage src={order.fabrics.image_url} alt="" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px' }} />}
              <div>
                <div style={{ fontSize: '10px', color: 'var(--gold)', fontWeight: '600' }}>{order.fabrics.fabric_code}</div>
                <div style={{ fontWeight: '600' }}>{order.fabrics.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>{order.fabrics.color}</div>
              </div>
            </div>
          )}
          {order.fabric_image_url && <PrivateImage src={order.fabric_image_url} alt="Fabric swatch" style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px' }} />}
          {order.fabric_notes && <p style={{ fontSize: '13px', color: 'var(--gray-600)' }}>{order.fabric_notes}</p>}
          {order.lining_notes && <p style={{ fontSize: '13px', color: 'var(--gray-600)', marginTop: '4px' }}><span style={{ fontWeight: '600' }}>Lining: </span>{order.lining_notes}</p>}
        </div>
      )}

      {/* Scanned sheet */}
      {order.handwritten_scan_url && (
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Handwritten Sheet</div>
          <PrivateImage src={order.handwritten_scan_url} alt="Order scan" style={{ width: '100%', borderRadius: '8px' }} />
        </div>
      )}

      {/* Style reference */}
      {order.style_image_url && (
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Style Reference</div>
          <PrivateImage src={order.style_image_url} alt="Style reference" style={{ width: '100%', borderRadius: '8px' }} />
        </div>
      )}

      {order.notes && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="form-section-title">Notes</div>
          <p style={{ fontSize: '13px', color: 'var(--gray-700)', lineHeight: '1.5' }}>{order.notes}</p>
        </div>
      )}
    </div>
  )
}

export default Orders
